import type { RouteLocationNormalized, RouterScrollBehavior, RouteRecordRaw } from 'vue-router'
import HomeView from '@/views/HomeView.vue'
import ProductView from '@/views/ProductView.vue'
import ThankYouView from '@/views/ThankYouView.vue'
import PaymentLinkView from '@/views/PaymentLinkView.vue'
import LoginView from '@/views/LoginView.vue'
import ForgotPasswordView from '@/views/ForgotPasswordView.vue'
import AccountView from '@/views/AccountView.vue'
import AccountSubView from '@/views/AccountSubView.vue'
import ContactView from '@/views/ContactView.vue'
import ContractsView from '@/views/ContractsView.vue'
import CardView from '@/views/CardView.vue'
import DocumentsView from '@/views/DocumentsView.vue'
import InfoView from '@/views/InfoView.vue'
import CredentialsView from '@/views/CredentialsView.vue'
import PaymentView from '@/views/PaymentView.vue'
import SubscribeView from '@/views/SubscribeView.vue'
import ReimbursementsView from '@/views/ReimbursementsView.vue'
import { productSlugs, PRODUCT_KEYS, staticPageSlugs, accountSectionSlugs, swapLocaleInPath, type ProductKey, type AccountSection } from './products'
import { getStoredLocale, localizedPath, type Locale } from '@/i18n'
import { SCROLL_TOP } from '@/utils/scroll'

declare module 'vue-router' {
  interface RouteMeta {
    locale: Locale
    productKey?: ProductKey
    /** 'account' = espace personnel : chrome dédié (sidebar), header public masqué. */
    area?: 'account'
    /** Sous-page de l'espace perso (clé de section, ex. 'contracts'). */
    accountSection?: AccountSection
  }
}

const LOCALES: readonly Locale[] = ['fr', 'en']

/**
 * Une route par produit × locale, avec un catch-all `:answerPath*` qui capture
 * les slugs de réponses successives. L'URL est l'unique source de vérité pour
 * l'état du parcours.
 *
 * Exemples :
 *   /depenses-sante                    → step 1 (aucune réponse)
 *   /depenses-sante/moi                → step 2 (forWhom=self)
 *   /depenses-sante/moi/oui            → step 3 (forWhom=self, isStudent=yes)
 *   /en/health-expenses/me/yes         → équivalent EN
 */
const productRoutes: RouteRecordRaw[] = PRODUCT_KEYS.flatMap((productKey) =>
  LOCALES.map<RouteRecordRaw>((locale) => {
    const prefix = locale === 'fr' ? '' : `/${locale}`
    const productSlug = productSlugs[productKey][locale]
    return {
      path: `${prefix}/${productSlug}/:answerPath*`,
      component: ProductView,
      meta: { locale, productKey },
    }
  }),
)

/** Thank-you page atteinte après paiement (redirect Stripe côté back).
 *  Slug FR : /merci · EN : /en/thank-you. URL stable pour que le back puisse
 *  rediriger l'utilisateur après succès. */
const thankYouRoutes: RouteRecordRaw[] = [
  { path: '/merci',        name: 'thank-you-fr', component: ThankYouView, meta: { locale: 'fr' } },
  { path: '/en/thank-you', name: 'thank-you-en', component: ThankYouView, meta: { locale: 'en' } },
]

/** Page de confirmation « lien de paiement partagé » : le lien Stripe est généré
 *  à l'ouverture depuis le contexte transmis par l'étape récap (cf. usePaymentLink).
 *  Slugs localisés (FR : /lien-de-paiement · EN : /en/payment-link). */
const paymentLinkRoutes: RouteRecordRaw[] = [
  { path: '/lien-de-paiement', name: 'payment-link-fr', component: PaymentLinkView, meta: { locale: 'fr' } },
  { path: '/en/payment-link',  name: 'payment-link-en', component: PaymentLinkView, meta: { locale: 'en' } },
]

/** Sous-pages de l'espace perso (contrats, documents, identifiants…) : pages
 *  vides pour l'instant, une route par section × locale, slugs localisés.
 *  Réservées (garde côté composant) → hors pré-rendu SSG comme la racine. */
const ACCOUNT_SECTIONS = Object.keys(accountSectionSlugs) as AccountSection[]
/** Vue dédiée d'une section ; celles qui n'en ont pas encore tombent sur `AccountSubView`. */
const ACCOUNT_SECTION_VIEWS: Partial<Record<AccountSection, RouteRecordRaw['component']>> = {
  contact: ContactView,
  contracts: ContractsView,
  card: CardView,
  documents: DocumentsView,
  reimbursements: ReimbursementsView,
  info: InfoView,
  credentials: CredentialsView,
  payment: PaymentView,
  subscribe: SubscribeView,
}
const accountSectionRoutes: RouteRecordRaw[] = ACCOUNT_SECTIONS.flatMap((section) =>
  LOCALES.map<RouteRecordRaw>((locale) => {
    const prefix = locale === 'fr' ? '' : `/${locale}`
    const base = `${prefix}/${staticPageSlugs.account[locale]}`
    const slug = accountSectionSlugs[section][locale]
    return {
      path: `${base}/${slug}`,
      name: `account-${section}-${locale}`,
      component: ACCOUNT_SECTION_VIEWS[section] ?? AccountSubView,
      meta: { locale, area: 'account', accountSection: section },
    }
  }),
)
/** Clé localStorage mémorisant le dernier parcours visité (chemin complet, qui
 *  encode toutes les réponses), pour ramener l'utilisateur à son étape
 *  récapitulative après une annulation Stripe. */
export const SUBSCRIPTION_RETURN_KEY = 'smerra:journey:return'

/** Dernier chemin de parcours mémorisé, ou null. On n'accepte qu'un chemin
 *  interne (commençant par « / ») pour éviter toute redirection ouverte. */
function getStoredSubscriptionPath(): string | null {
  if (typeof localStorage === 'undefined') return null
  const stored = localStorage.getItem(SUBSCRIPTION_RETURN_KEY)
  return stored && stored.startsWith('/') ? stored : null
}

/** URLs de retour Stripe configurées côté back (`frontend_app_url + …`). Ces
 *  chemins externes ne portent pas la locale ni l'état du parcours, on s'appuie
 *  donc sur ce qui a été mémorisé avant le départ vers Stripe.
 *  - succès → page de remerciement, dans la dernière langue chargée ;
 *  - annulation → étape récapitulative du dernier parcours (repli : accueil). */
const subscriptionReturnRoutes: RouteRecordRaw[] = [
  {
    path: '/subscription/end',
    redirect: () => {
      const locale = getStoredLocale()
      return localizedPath(`/${staticPageSlugs.thankYou[locale]}`, locale)
    },
  },
  {
    // On retombe sur le chemin du parcours (qui encode toutes les réponses) ;
    // le formulaire infos restauré depuis sessionStorage ré-affiche le récap, et
    // sa pile de sous-étapes est reconstruite à l'hydratation (cf. SubscriptionStartCard)
    // pour que le « Précédent » du navigateur remonte sous-étape par sous-étape.
    path: '/subscription/summary',
    redirect: () => getStoredSubscriptionPath() ?? localizedPath('/', getStoredLocale()),
  },
]

/** Espace personnel : page de connexion + parcours « mot de passe oublié ».
 *  Slugs localisés (FR : /connexion · /mot-de-passe-oublie). */
const authRoutes: RouteRecordRaw[] = [
  { path: '/connexion',            name: 'login-fr',  component: LoginView,          meta: { locale: 'fr' } },
  { path: '/en/login',             name: 'login-en',  component: LoginView,          meta: { locale: 'en' } },
  { path: '/mot-de-passe-oublie',  name: 'forgot-fr', component: ForgotPasswordView, meta: { locale: 'fr' } },
  { path: '/en/forgot-password',   name: 'forgot-en', component: ForgotPasswordView, meta: { locale: 'en' } },
  // Initialisation du mot de passe : même vue, déclenchée par le token en query
  // (lien reçu par email). Slugs localisés, token-gated → non pré-rendus (SSG).
  { path: `/${staticPageSlugs.resetPassword.fr}`,       name: 'reset-fr', component: ForgotPasswordView, meta: { locale: 'fr' } },
  { path: `/en/${staticPageSlugs.resetPassword.en}`,    name: 'reset-en', component: ForgotPasswordView, meta: { locale: 'en' } },
  // Le lien email pointe vers l'URL fixe /reset-password (sans locale) : on
  // redirige vers le slug localisé selon la langue mémorisée, en conservant le
  // token de la query.
  {
    path: '/reset-password',
    redirect: (to) => {
      const locale = getStoredLocale()
      return {
        path: localizedPath(`/${staticPageSlugs.resetPassword[locale]}`, locale),
        query: to.query,
      }
    },
  },
  // Espace personnel — réservé (garde côté composant), donc non pré-rendu (SSG).
  { path: '/espace-personnel',     name: 'account-fr', component: AccountView, meta: { locale: 'fr', area: 'account' } },
  { path: '/en/my-account',        name: 'account-en', component: AccountView, meta: { locale: 'en', area: 'account' } },
  ...accountSectionRoutes,
]

export const routes: RouteRecordRaw[] = [
  { path: '/',    name: 'home-fr', component: HomeView, meta: { locale: 'fr' } },
  { path: '/en/', name: 'home-en', component: HomeView, meta: { locale: 'en' } },
  ...thankYouRoutes,
  ...paymentLinkRoutes,
  ...subscriptionReturnRoutes,
  ...authRoutes,
  ...productRoutes,
]

/** True when `to` is the very same page as `from`, only in the other language. */
function isLocaleSwap(to: RouteLocationNormalized, from: RouteLocationNormalized): boolean {
  if (!from.meta?.locale || from.meta.locale === to.meta?.locale) return false
  return swapLocaleInPath(from.path, to.meta.locale) === to.path
}

/**
 * Every navigation lands at the top of the page, on mobile as on desktop — a
 * journey step or an account sub-page must never open mid-scroll. The saved
 * position is deliberately ignored, so a browser Back also starts at the top.
 *
 * Two exceptions: an anchor in the URL wins, and a language swap keeps the
 * reading position since it is the same page in the other locale.
 *
 * Screens that share a single URL (the « Tes informations » sub-steps) never
 * reach this hook: they call `scrollToTop()` themselves.
 */
export const scrollBehavior: RouterScrollBehavior = (to, from) => {
  if (to.hash) return { el: to.hash, behavior: SCROLL_TOP.behavior }
  if (isLocaleSwap(to, from)) return false
  return SCROLL_TOP
}
